// Inline mention token: @[Full Name](user:<uuid>)
export const MENTION_RE = /@\[([^\]]+)\]\(user:([0-9a-fA-F-]{36})\)/g

export function parseMentionUserIds(body: string): string[] {
  const ids = new Set<string>()
  for (const m of body.matchAll(MENTION_RE)) ids.add(m[2]!)
  return [...ids]
}

/**
 * Mentioned user id -> the display name written in its token, in
 * first-appearance order. The name is whatever the author's editor wrote; use
 * it for the author's own UI copy, never as proof of identity.
 */
export function parseMentionNamesByUserId(body: string): Map<string, string> {
  const seen = new Map<string, string>()
  for (const m of body.matchAll(MENTION_RE)) {
    if (!seen.has(m[2]!)) seen.set(m[2]!, m[1]!)
  }
  return seen
}

export function renderMentionsToText(body: string): string {
  return body.replace(MENTION_RE, (_all, name: string) => `@${name}`)
}

// Minimal shape of a TipTap/ProseMirror document node (JSON).
export interface TiptapDocNode {
  type: string
  text?: string
  attrs?: Record<string, unknown> | null
  content?: TiptapDocNode[]
}

/**
 * Serialize a TipTap editor document (JSON) into the plain-text comment body,
 * rendering `mention` nodes as the backend token `@[Label](user:<id>)`.
 *
 * Block nodes (paragraphs) are joined with newlines; inline nodes within a
 * block are concatenated. Unknown inline nodes are ignored.
 */
export function serializeDocToBody(doc: TiptapDocNode | null | undefined): string {
  if (!doc?.content?.length) return ''
  return doc.content.map(serializeNode).join('\n').replace(/\n{3,}/g, '\n\n').trim()
}

/**
 * Inverse of `serializeDocToBody`: rebuild a TipTap document (JSON) from a
 * stored comment body, turning `@[Label](user:<id>)` tokens back into `mention`
 * nodes so an editor can be prefilled with an existing comment.
 *
 * Newline-separated lines become separate paragraphs, mirroring the `\n` join
 * the serializer uses for block nodes.
 */
export function parseBodyToDoc(body: string): TiptapDocNode {
  return {
    type: 'doc',
    content: body.split('\n').map((line) => {
      const content = parseLineToNodes(line)
      return content.length ? { type: 'paragraph', content } : { type: 'paragraph' }
    }),
  }
}

function parseLineToNodes(line: string): TiptapDocNode[] {
  const nodes: TiptapDocNode[] = []
  let lastIndex = 0

  // MENTION_RE is a global regex — reset lastIndex so repeated calls are safe.
  MENTION_RE.lastIndex = 0
  for (const match of line.matchAll(MENTION_RE)) {
    const [token, label, userId] = match
    const start = match.index ?? 0
    if (start > lastIndex) nodes.push({ type: 'text', text: line.slice(lastIndex, start) })
    nodes.push({ type: 'mention', attrs: { id: userId, label } })
    lastIndex = start + token.length
  }
  if (lastIndex < line.length) nodes.push({ type: 'text', text: line.slice(lastIndex) })

  return nodes
}

function serializeNode(node: TiptapDocNode | null | undefined): string {
  if (!node) return ''
  if (node.type === 'text') return node.text ?? ''
  if (node.type === 'mention') {
    const attrs = node.attrs ?? {}
    const id = attrs['id']
    const label = attrs['label'] ?? attrs['id'] ?? ''
    if (typeof id === 'string' && id) {
      return `@[${String(label)}](user:${id})`
    }
    return label ? `@${String(label)}` : ''
  }
  if (node.content?.length) return node.content.map(serializeNode).join('')
  return ''
}
